import { useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { r2Upload } from '@/lib/r2';
import { toast } from 'sonner';

interface SignaturePadProps {
  aoId: string;
  type: 'tech' | 'manager';
  signedBy: string; // technicians.id
  onSigned: (url: string, signedAt: string) => void;
  onCancel: () => void;
}

export function SignaturePad({ aoId, type, signedBy, onSigned, onCancel }: SignaturePadProps) {
  const sigRef = useRef<ReactSignatureCanvas>(null);
  const [saving, setSaving] = useState(false);

  const handleClear = () => sigRef.current?.clear();

  const handleSave = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please draw your signature before saving.');
      return;
    }

    setSaving(true);
    try {
      // Export as PNG blob
      const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'image/png' });

      const signedAt = new Date().toISOString();
      const path = `${aoId}/${type}_${Date.now()}.png`;

      const publicUrl = await r2Upload(`signatures/${path}`, blob, 'image/png');

      // Save URL + metadata to assembly_orders
      const col = type === 'tech' ? 'tech' : 'manager';
      const { error: dbError } = await supabase
        .from('assembly_orders')
        .update({
          [`${col}_signature_url`]: publicUrl,
          [`${col}_signed_at`]: signedAt,
          [`${col}_signed_by`]: signedBy,
        })
        .eq('id', aoId);

      if (dbError) throw dbError;

      onSigned(publicUrl, signedAt);
      toast.success('Signature saved.');
    } catch (err: any) {
      toast.error(`Failed to save signature: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {type === 'tech' ? 'Technician sign-off' : 'Manager / QC sign-off'} — draw your signature below.
      </p>
      <div className="border rounded-md overflow-hidden bg-white">
        <ReactSignatureCanvas
          ref={sigRef}
          penColor="#111827"
          canvasProps={{
            width: 460,
            height: 160,
            className: 'w-full touch-none',
          }}
        />
      </div>
      <div className="flex justify-between">
        <Button variant="outline" size="sm" onClick={handleClear} disabled={saving}>
          Clear
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm Signature'}
          </Button>
        </div>
      </div>
    </div>
  );
}
